import React, { useMemo, useState } from 'react';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import firebase from 'utils/firebase';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateAlertModal from './CreateAlertModal';
import useCollection from 'CustomHooks/useCollection';

function toJsDate(date: firebase.firestore.Timestamp | Date): Date {
  return date instanceof firebase.firestore.Timestamp ? date.toDate() : date;
}

type AlertDoc = {
  description: string;
  isGeneral?: boolean;
  restaurantId?: string;
  createdAt: firebase.firestore.Timestamp | null;
  severity?: 'error' | 'warning' | 'info';
};

const getSeverityColor = (sev: 'error' | 'warning' | 'info' | undefined) => {
  switch (sev) {
    case 'warning':
      return '#ff9800';
    case 'info':
      return '#2196f3';
    case 'error':
    default:
      return '#f44336';
  }
};

const AlertItem: React.FC<{
  alert: AlertDoc & { id: string };
  onDelete: (id: string) => void;
}> = ({ alert, onDelete }) => (
  <Box
    elevation={1}
    style={{
      padding: 12,
      borderRadius: 8,
      marginTop: 8,
      position: 'relative',
      borderLeft: `4px solid ${getSeverityColor(alert.severity)}`,
    }}
    background
  >
    <div
      style={{ margin: '4px 0 0' }}
      dangerouslySetInnerHTML={{ __html: alert.description }}
    />
    <small style={{ opacity: 0.6 }}>
      {alert.isGeneral ? '' : `${alert?.restaurantId}`} •{' '}
      {alert.createdAt ? toJsDate(alert.createdAt).toLocaleString() : ''}
    </small>
    <DeleteIcon
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        cursor: 'pointer',
        color: '#f44336',
      }}
      onClick={() => onDelete(alert.id)}
      titleAccess="Alert löschen"
    />
  </Box>
);

const AlertsTab: React.FC<{ restaurantId: string }> = ({ restaurantId }) => {
  const [createModalOpen, setcreateModalOpen] = useState(false);

  // Two filtered subscriptions instead of one unfiltered query: the Firestore
  // rule denies the whole collection read unless every matched doc is readable
  // (isGeneral || waiter of restaurantId). Each single-field `where` is
  // auto-indexed; we drop `orderBy` to avoid a composite index and sort/limit
  // client-side.
  const [globalAlertsCol] = useCollection<AlertDoc>('dashboardAlertMessages', {
    filter: [['isGeneral', '==', true]],
  });

  const [restaurantAlertsCol] = useCollection<AlertDoc>(
    'dashboardAlertMessages',
    {
      filter: [['restaurantId', '==', restaurantId ?? '']],
    }
  );

  const sortByCreatedAtDesc = (
    alerts: (AlertDoc & { id: string })[]
  ): (AlertDoc & { id: string })[] =>
    [...alerts]
      .sort(
        (a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0)
      )
      .slice(0, 100);

  const handleDeleteAlert = async (id: string) => {
    try {
      await firebase
        .firestore()
        .collection('dashboardAlertMessages')
        .doc(id)
        .delete();
    } catch (err) {
      console.error('Fehler beim Löschen des Alerts:', err);
    }
  };

  // Filter alerts based on the current restaurant and general alerts
  const restaurantAlerts = useMemo(
    () =>
      sortByCreatedAtDesc(
        (restaurantAlertsCol.data ?? []).filter(
          (alert) => !alert.isGeneral && alert.restaurantId === restaurantId
        )
      ),
    [restaurantAlertsCol.data, restaurantId]
  );

  const globalAlerts = useMemo(
    () =>
      sortByCreatedAtDesc(
        (globalAlertsCol.data ?? []).filter((alert) => alert.isGeneral)
      ),
    [globalAlertsCol.data]
  );

  return (
    <Box flex style={{ flexDirection: 'column', gap: 16 }}>
      <Box flex style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button variant="primary" onClick={() => setcreateModalOpen(true)}>
          + Alert erstellen
        </Button>
      </Box>

      {globalAlerts.length === 0 && restaurantAlerts.length === 0 && (
        <p style={{ opacity: 0.7, textAlign: 'center', marginTop: 24 }}>
          Es gibt keine Alerts anzuzeigen.
        </p>
      )}

      {globalAlerts.length > 0 && (
        <Box>
          <h3>Globale Alerts</h3>
          <Box style={{ maxHeight: 160, overflowY: 'auto' }}>
            {globalAlerts.map((alert) => (
              <AlertItem
                key={alert.id}
                alert={alert}
                onDelete={handleDeleteAlert}
              />
            ))}
          </Box>
        </Box>
      )}

      {restaurantAlerts.length > 0 && (
        <Box>
          <h3>Restaurant-Alerts</h3>
          <Box style={{ maxHeight: 160, overflowY: 'auto' }}>
            {restaurantAlerts.map((alert) => (
              <AlertItem
                key={alert.id}
                alert={alert}
                onDelete={handleDeleteAlert}
              />
            ))}
          </Box>
        </Box>
      )}
      <CreateAlertModal
        open={createModalOpen}
        onClose={() => setcreateModalOpen(false)}
        restaurantId={restaurantId}
      />
    </Box>
  );
};

export default AlertsTab;
